import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {CreateProjectPayload} from '@taopix/taopix-core/dist/types/Generator/Creator/index';
import {CollectionLayout} from '../../Product/LayoutSelector';
import useSettings from '../../../Hooks/useSettings';
import {LoaderStore} from '@taopix/taopix-core/dist/types/Generator/Loader';

export type ProjectPreviewState = {
  layout: CollectionLayout;
  previews: string[];
  info: CreateProjectPayload & {extraData?: string};
  dimensions: {
    height: number;
    width: number;
  };
  open: boolean;
  store?: LoaderStore;
};

export type ProductsContextState = {
  state: ProjectPreviewState[];
  purge: () => void;
  add: (project: ProjectPreviewState) => Promise<any>;
  remove: (projectRef: string) => void;
  getEntry: (layout: CollectionLayout) => ProjectPreviewState | undefined;
};

export const ProjectCacheContext = createContext<ProductsContextState>({} as ProductsContextState);

export const ProjectCacheProvider = (props: {children: any}) => {
  const {children} = props;
  const [state, setState] = useState<ProjectPreviewState[]>([]);
  const settings = useSettings();

  const urlFormat = useMemo(
    () => settings.state.productTheming.deleteUrlFormat,
    [settings.state.productTheming.deleteUrlFormat]
  );

  const callbackUrl = useMemo(
    () => settings.state.urls.callbackUrl,
    [settings.state.urls.callbackUrl]
  );

  const extraData = useMemo(
    () => settings.state.authentication.extraData,
    [settings.state.authentication.extraData]
  );

  const currentProjectRef = useMemo(() => settings.state.project.ref, [settings.state.project.ref]);

  const add = (project: ProjectPreviewState) => {
    console.log('adding', project.info.projectref);
    return new Promise((resolve, reject) => {
      setState(current => {
        current.push(project);
        return current;
      });
      setTimeout(() => resolve(true), 500);
    });
  };

  const remove = useCallback(
    (projectRef: string) => {
      console.log('removing', projectRef);
      setState(current => {
        return current.filter(entry => String(entry.info.projectref) !== String(projectRef));
      });
    },
    [state]
  );

  const purge = useCallback(() => {
    if (0 === state.length) {
      return;
    }

    const payload = [...state.map(entry => entry.info.projectref)];
    console.log('purging', payload);

    if (0 === payload.length) {
      return;
    }

    const url = urlFormat.replace('[url]', callbackUrl);
    const data = JSON.stringify({
      projectreflist: payload,
      ed: extraData,
    });

    navigator.sendBeacon(url, data);
  }, [state, urlFormat, callbackUrl, currentProjectRef, extraData]);

  const getEntry = useCallback(
    (theme: CollectionLayout) => {
      return state.find(
        entry => entry.layout.collection.collectionCode === theme.collection.collectionCode
      );
    },
    [state]
  );

  const contextValue = useMemo(
    () => ({
      state,
      add,
      purge,
      getEntry,
      remove,
    }),
    [state, add, purge, getEntry]
  );

  useEffect(() => {
    window.addEventListener('beforeunload', purge, true);

    return () => {
      window.removeEventListener('beforeunload', purge, true);
    };
  }, [purge]);

  return (
    <ProjectCacheContext.Provider value={contextValue}>{children}</ProjectCacheContext.Provider>
  );
};

export const useProjectCache = () => {
  const context = useContext(ProjectCacheContext);
  if (context === undefined) {
    throw new Error('useProjectsContext must be used within a ProjectsProvider');
  }
  return context;
};
